import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useSwyftxMargin } from 'src/lib/margin';
import { useMarginAccount } from 'src/lib/margin/hooks/account';

type Props = {
  hideProfitLoss?: boolean;
};

const MarginAccountSummaryEquity: React.FC<Props> = ({ hideProfitLoss }) => {
  const { profitLossTotal, equity, currency, isLoading } = useMarginAccount();
  const { isUnderMaintenance } = useSwyftxMargin();

  const { t } = useTranslation('margin');

  return (
    <FlexLayout direction='column' className='w-full'>
      <EnhancedTooltip
        name={t('accountOverview.equity.name')}
        title={t('accountOverview.equity.tooltip')}
        align='start'
        side='top'
      >
        <Body underline='dashed' color='secondary' size='xsmall' className='w-fit'>
          {t('accountOverview.equity.title')}
        </Body>
      </EnhancedTooltip>

      <FlexLayout direction='column' spacing={0} className='w-full'>
        <Numeric size='xxlarge' weight='bold' loading={isLoading}>
          {isUnderMaintenance && !equity
            ? t('maintenance.labels.unavailable')
            : formatCurrency(equity, currency, { appendCode: true, hideCode: false })}
        </Numeric>
        {!hideProfitLoss && (
          <Numeric
            size='hero'
            weight='extraBold'
            color={Big(profitLossTotal).gt(0) ? 'success' : 'error'}
            loading={isLoading}
          >
            {Big(profitLossTotal).gt(0) ? '+' : ''}
            {formatCurrency(profitLossTotal, currency, { appendCode: true, hideCode: false })}
          </Numeric>
        )}
      </FlexLayout>
    </FlexLayout>
  );
};

export { MarginAccountSummaryEquity };
